import { IconButton, useColorModeValue } from "@chakra-ui/react";
import { FiEdit2 } from "react-icons/fi";
import { AutoFormSection } from "models/autoForm";
import V2ModalForm from "components/ModalForm/V2ModalForm";
import NewHireAutoSaveForm from "features/Form/Components/NewHireAutoSaveForm";
import { useQueryClient } from "@tanstack/react-query";
import { newHireJourneyKeys } from "../hooks";

interface FormSchema {
  sections: AutoFormSection[];
}

export const MANAGER_FIELD_SCHEMA = {
  sections: [
    {
      title: "Select Manager",
      emoji: "👤",
      fields: [
        {
          name: "managerId",
          type: "userSelect",
          label: "Manager",
          accessor: "manager.id",
          writer: "newHireJourney.managerId",
        },
      ],
    },
  ],
} as FormSchema;

export const BUDDY_FIELD_SCHEMA = {
  sections: [
    {
      title: "Select Buddy",
      emoji: "🤝",
      fields: [
        {
          name: "buddyId",
          type: "userSelect",
          label: "Buddy",
          accessor: "buddyId",
          writer: "newHireJourney.buddyId",
        },
      ],
    },
  ],
} as FormSchema;

export const PEOPLE_TEAM_CONTACT_FIELD_SCHEMA = {
  sections: [
    {
      title: "Select People Team Contact",
      emoji: "💬",
      fields: [
        {
          name: "peopleTeamContactId",
          type: "userSelect",
          label: "People Team Contact",
          accessor: "peopleTeamContactId",
          writer: "newHireJourney.peopleTeamContactId",
        },
      ],
    },
  ],
} as FormSchema;

export const RECRUITER_FIELD_SCHEMA = {
  sections: [
    {
      title: "Select Recruiter",
      emoji: "🧭",
      fields: [
        {
          name: "recruiterId",
          type: "userSelect",
          label: "Recruiter",
          accessor: "recruiterId",
          writer: "newHireJourney.recruiterId",
        },
      ],
    },
  ],
} as FormSchema;

export const IT_TEAM_CONTACT_FIELD_SCHEMA = {
  sections: [
    {
      title: "Select IT Team Contact",
      emoji: "💻",
      fields: [
        {
          name: "itTeamContactId",
          type: "userSelect",
          label: "IT Team Contact",
          accessor: "itTeamContactId",
          writer: "newHireJourney.itTeamContactId",
        },
      ],
    },
  ],
} as FormSchema;

interface EditSupportCrewMemberButtonProps {
  newHireJourneyId: string;
  formSchema: FormSchema;
  label: string;
  onSuccess?: () => void;
  renderTrigger?: (onOpen: () => void) => React.ReactNode;
}

export default function EditSupportCrewMemberButton({
  newHireJourneyId,
  formSchema,
  label,
  onSuccess,
  renderTrigger,
}: EditSupportCrewMemberButtonProps) {
  const queryClient = useQueryClient();
  const bgColor = useColorModeValue("creamBg.500", "gray.500");

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(newHireJourneyId) });
    queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
    onSuccess?.();
  };

  return (
    <V2ModalForm
      includeCloseButton={false}
      closeOnOverlayClick
      size="md"
      modalContentProps={{ bgColor }}
      renderButton={(onOpen) =>
        renderTrigger ? (
          renderTrigger(onOpen)
        ) : (
          <IconButton
            aria-label={`Edit ${label.toLowerCase()}`}
            icon={<FiEdit2 />}
            size="xs"
            variant="ghost"
            colorScheme="gray"
            onClick={onOpen}
          />
        )
      }
      renderForm={(onClose) => (
        <NewHireAutoSaveForm
          newHireId={newHireJourneyId}
          formSchema={formSchema}
          onSuccess={() => {
            handleSuccess();
            onClose();
          }}
        />
      )}
    />
  );
}
